import * as React from "react";
import * as S from "@styled";
import Viewer from "bpmn-js/lib/NavigatedViewer";
import Canvas from "diagram-js/lib/core/Canvas";
import modeling from "bpmn-js/lib/features/modeling";
import Modeling from "bpmn-js/lib/features/modeling/Modeling";
import { Element } from "bpmn-js/lib/model/Types";
import styled, { DefaultTheme, useTheme } from "styled-components";
import Button from "components/shared/Button";
import DmnCustomRenderer from "./DmnCustomRendered";
import { ProcessElementParameters } from "model/process/ProcessElementParameters";
import ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import {
    ElementType,
    NodeStatusType,
    RenderStateResolver,
} from "./RenderStateResolver";
import { StepData } from "../../../model/process/StepData";
import { Colors } from "bpmn-js/lib/util/Types";

const BpmnViewer: React.FC<BpmnViewerProps> = (props) => {
    const viewerRef = React.useRef<Viewer>();
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const dmnElementIds = React.useMemo(
        () =>
            new Set(
                props.elementParameters
                    ?.filter((e) => e.type.toLowerCase() === "dmn")
                    .map((e) => e.id) ?? [],
            ),
        [props.elementParameters],
    );

    const theme = useTheme();

    const importXml = React.useCallback(async () => {
        if (!containerRef.current) {
            return;
        }

        const dmnRendererName = "dmnCustomRenderer";

        if (!viewerRef.current) {
            viewerRef.current = new Viewer({
                container: containerRef.current,
                additionalModules: [
                    {
                        __init__: [dmnRendererName],
                        dmnCustomRenderer: ["type", DmnCustomRenderer],
                    },
                    modeling,
                ],
            });
        }

        viewerRef.current
            .get<DmnCustomRenderer>(dmnRendererName)
            .setDmnElementIds(dmnElementIds);

        await viewerRef.current.importXML(props.bpmnXml);
        const canvas = viewerRef.current.get<Canvas>("canvas");
        canvas.zoom("fit-viewport");

        if (props.stepData && viewerRef.current) {
            setElementColorsBasedOnExecutionState(
                props.stepData,
                viewerRef.current,
                props.elementDictionary,
                theme,
            );
        }
    }, [
        props.bpmnXml,
        dmnElementIds,
        props.stepData,
        props.elementDictionary,
        theme,
    ]);

    React.useEffect(() => {
        if (props.bpmnXml) {
            importXml();
        }
    }, [importXml, props.bpmnXml]);

    const handleResetZoom = () => {
        if (viewerRef.current) {
            const canvas = viewerRef.current.get<Canvas>("canvas");
            canvas.zoom("fit-viewport");
        }
    };

    return (
        <BpmnContainer>
            <S.GridContainer $justifyContent="right">
                <Button onClick={handleResetZoom}>Reset Zoom</Button>
            </S.GridContainer>
            <div ref={containerRef} style={{ height: "400px" }} />
        </BpmnContainer>
    );
};

function setElementColorsBasedOnExecutionState(
    stepData: StepData,
    viewer: Viewer,
    elementDictionary: Map<string, ProcessElementParameters> | undefined,
    theme: DefaultTheme,
) {
    const localElementRegistry: {
        [key: string]: Element;
    } = {};

    const initialIndexes = new Map<string, number>();
    if (stepData.loops) {
        for (const loopId of Object.keys(stepData.loops)) {
            initialIndexes[loopId] = 0;
        }
    }

    const executedStepStatuses = getExecutedStepStatusesForCurrentLoopIndexes(
        stepData,
        initialIndexes,
    );

    const registry = viewer.get<ElementRegistry>("elementRegistry");

    registry.forEach((element) => {
        localElementRegistry[element.id] = element as Element;
    });

    const typeResolver = (stepId: string) => getType(stepId, elementDictionary);
    const renderState = RenderStateResolver.GetRenderResultFromStatus(
        localElementRegistry,
        executedStepStatuses,
        typeResolver,
    );

    const elementsWithChangedColorByState: {
        [nodeStatusType: string]: Element[];
    } = {};

    for (const key of Object.keys(renderState)) {
        const state = renderState[key];
        const elem = localElementRegistry[state.id];
        const expectedFillAndStroke = stateToClass(state.nodeStatus, theme);
        let doSetColor = true;
        if (elem && elem.di) {
            doSetColor =
                expectedFillAndStroke?.fill !== elem.di.fill ||
                expectedFillAndStroke?.stroke !== elem.di.stroke;
        }
        if (doSetColor && state.nodeStatus) {
            if (
                !elementsWithChangedColorByState[
                    NodeStatusType[state.nodeStatus]
                ]
            ) {
                elementsWithChangedColorByState[
                    NodeStatusType[state.nodeStatus]
                ] = [];
            }
            elementsWithChangedColorByState[
                NodeStatusType[state.nodeStatus]
            ].push(elem);
        }
    }

    const modeling = viewer.get<Modeling>("modeling");

    for (const elemState of Object.keys(elementsWithChangedColorByState)) {
        const colors = stateToClass(NodeStatusType[elemState], theme);
        if (colors) {
            const elements = elementsWithChangedColorByState[elemState];
            modeling?.setColor(elements, colors);
        }
    }
}

function getExecutedStepStatusesForCurrentLoopIndexes(
    stepData: StepData,
    currentIndexSelections: Map<string, number>,
) {
    let result: Map<string, string | undefined> | undefined = new Map<
        string,
        string | undefined
    >();
    const loopIds = Object.keys(stepData.loops);
    if (!loopIds || loopIds.length < 1) {
        // no loops, just return all statuses
        if (stepData.stepData) {
            for (const key of Object.keys(stepData.stepData)) {
                const stepStatus = stepData.stepData[key];
                if (stepStatus) {
                    result[key] = stepStatus.state;
                }
            }
        } else {
            result = stepData.steps;
        }
    } else {
        for (const elementId of Object.keys(stepData.elementLoops)) {
            const stepKey = getStepKeyForCurrentLoop(
                elementId,
                stepData,
                currentIndexSelections,
            );
            const stepStatus = getStatusForStep(stepKey, stepData);
            if (stepStatus) {
                result[elementId] = stepStatus;
            }
        }
    }

    return result;
}

function getStepKeyForCurrentLoop(
    stepId: string,
    stepData: StepData,
    currentIndexSelections: Map<string, number>,
) {
    const loopsForElement = stepData?.elementLoops[stepId as string];
    if (!loopsForElement) {
        // no loop indexes, just the step ID
        return stepId;
    }

    let stepKey = stepId;
    for (const loopId of loopsForElement) {
        const indexForParentLoop =
            (currentIndexSelections && currentIndexSelections[loopId]) || 0;
        stepKey = `${stepKey}-${loopId}$${indexForParentLoop}`;
    }

    return stepKey;
}

function getStatusForStep(
    stepKey: string,
    stepData: StepData,
): string | undefined {
    if (stepData.stepData && stepData.stepData[stepKey as string]) {
        return stepData.stepData[stepKey as string].state;
    } else {
        // old style step status?
        return stepData?.steps && stepData.steps[stepKey as string];
    }
}

function getType(
    stepId: string,
    elementDictionary: Map<string, ProcessElementParameters> | undefined,
): ElementType | null {
    if (elementDictionary && elementDictionary[stepId]) {
        return ElementType[elementDictionary[stepId].type];
    }
    return null;
}

const BpmnContainer = styled.div`
    .bjs-container {
        *:focus {
            outline: none !important;
        }
    }
`;

interface BpmnViewerProps {
    bpmnXml: string;
    elementParameters?: ProcessElementParameters[];
    stepData?: StepData;
    elementDictionary?: Map<string, ProcessElementParameters>;
}

function stateToClass(
    state: NodeStatusType | undefined,
    theme: DefaultTheme,
): Colors | undefined {
    switch (state) {
        case NodeStatusType.Success:
            return {
                fill: theme["bpmnGreenLight"],
                stroke: theme["black"],
            };
        case NodeStatusType.Error:
            return {
                fill: theme["bpmnWarningLight"],
                stroke: theme["bpmnWarningDark"],
            };
        case NodeStatusType.Running:
            return {
                fill: theme["bpmnOrange"],
                stroke: theme["black"],
            };
        case NodeStatusType.NotRun:
            return {
                fill: theme["white"],
                stroke: theme["bpmnGrey"],
            };
        case NodeStatusType.ScopeRunning:
            return {
                fill: theme["white"],
                stroke: theme["bpmnOrange"],
            };
        case NodeStatusType.ScopeSuccess:
            return {
                fill: theme["white"],
                stroke: theme["bpmnGreenLight"],
            };
        case NodeStatusType.ScopeError:
            return {
                fill: theme["white"],
                stroke: theme["bpmnWarning"],
            };
        case NodeStatusType.ScopeNotRun:
            return {
                fill: theme["white"],
                stroke: theme["bpmnGrey"],
            };
    }
}

export default BpmnViewer;
